import axios from 'axios';
import { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { Customer, CustomerAddress } from '@lambda/apis/src/customer/types';
import {
  CustomerAddressType,
  CustomerSubscriptionChannel,
} from '@lambda/apis/src/customer/enum';
import {
  AuthTokens,
  CognitoCustomerResponse,
  CognitoRefreshTokenResponse,
  CreateCognitoCustomer,
} from '@lambda/apis/src/customer/cognito/types';
import { SignInAuthChallenge } from '@lambda/apis/src/customer/cognito/enum';
import { logoutCustomer } from '../analytics/trackCustomer';
import { logger } from '../logger';
import {
  UpdateEmailSubscriptionRequest,
  UpdateSmsSubscriptionRequest,
  UseCustomerResult,
} from './types';
import { hasSegmentConsent } from '../utils/cookie-consent';

const COOKIE_ANONYMOUS_ID = 'ajs_anonymous_id';

export function redirectLoginWithCallback(redirectURL?: string) {
  return {
    redirect: {
      destination: `/account/login${
        redirectURL !== '' ? `?redirectUrl=${redirectURL}` : null
      }`,
      permanent: false,
    },
  };
}

export const COOKIE_COGNITO_REFRESH_TOKEN = 'refresh-token';
export const COOKIE_COGNITO_ID_TOKEN = 'id-token';

// `httpOnly` can only be set via a server `Set-Cookie` header, which would
// require issuing these auth cookies server-side (a larger, separate piece
// of work - see RT-16989). `secure` and `sameSite`, however, are honoured by
// the browser regardless of whether the cookie was set by JS or by a
// server, so we set them here as an immediate hardening step.
//
// `secure` requires HTTPS, so it's disabled outside of production to avoid
// breaking `pnpm dev` (plain http://localhost).
//
// `sameSite: 'lax'` (rather than 'strict') is used because these cookies
// must still be readable on a normal top-level GET navigation that lands on
// the site from elsewhere - e.g. a logged-in customer clicking a link to
// their order/account pages from a transactional email - which a 'strict'
// cookie would omit on that first request.
export const getCognitoCookieOptions = () => ({
  path: '/',
  secure: process.env.NODE_ENV !== 'development',
  sameSite: 'lax' as const,
});

const useCustomer = (customerInfo: Customer | null): UseCustomerResult => {
  const [cookies, setCookie, removeCookie] = useCookies([
    COOKIE_ANONYMOUS_ID,
    COOKIE_COGNITO_REFRESH_TOKEN,
    COOKIE_COGNITO_ID_TOKEN,
  ]);

  const initialCustomer = customerInfo || undefined;
  const initialIdToken = cookies[COOKIE_COGNITO_ID_TOKEN] || null;
  const [customer, setCustomer] = useState<Customer | undefined>(
    initialCustomer,
  );
  const [anonymousId, setAnonymousId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasSegmentConsent()) {
      setAnonymousId(undefined);
      removeCookie(COOKIE_ANONYMOUS_ID, { path: '/' });

      return;
    }

    if (cookies.ajs_anonymous_id) {
      setAnonymousId(cookies.ajs_anonymous_id);
    }
  }, [cookies]);

  const setCustomerData = (c: Customer) => {
    setCustomer(c);
  };

  const clearCustomerData = () => {
    removeCookie(COOKIE_COGNITO_ID_TOKEN, { path: '/' });
    removeCookie(COOKIE_COGNITO_REFRESH_TOKEN, { path: '/' });
    setCustomer(undefined);
  };

  const setCognitoIdToken = (idToken: string) => {
    setCookie(COOKIE_COGNITO_ID_TOKEN, idToken, getCognitoCookieOptions());
  };

  const setCognitoRefreshToken = (refreshToken: string) => {
    setCookie(
      COOKIE_COGNITO_REFRESH_TOKEN,
      refreshToken,
      getCognitoCookieOptions(),
    );
  };

  const logout = async () => {
    if (!customer) {
      return;
    }

    await logoutCustomer();

    try {
      localStorage.removeItem('ajs_user_id');
      document.cookie = 'ajs_user_id=; Max-Age=0; path=/;';
    } catch (err) {
      logger.error({ err }, 'Failed to remove ajs_user_id');
    }

    clearCustomerData();
  };

  const updateAddress = async (
    type: CustomerAddressType,
    address: CustomerAddress,
  ): Promise<Customer | undefined> => {
    try {
      setLoading(true);

      const response = await axios.put<Customer>(
        `/api/customer/address?type=${type}`,
        address,
      );

      if (!response.data) {
        throw new Error('Failed to update address');
      }

      setCustomerData(response.data);
    } catch (err) {
      setError(
        'There was a problem updating your address. Please try again later.',
      );

      logger.error({ err }, 'Error updating address');
    } finally {
      setLoading(false);
    }

    return undefined;
  };

  /**
   * Saves the signed-in customer's legal business name. Returns the updated
   * customer, or undefined when it couldn't be saved.
   */
  const updateBusinessName = async (
    businessName: string,
  ): Promise<Customer | undefined> => {
    try {
      setLoading(true);

      const response = await axios.patch<Customer>('/api/customer/business', {
        businessName,
      });

      if (!response.data) {
        throw new Error('Failed to update business name');
      }

      // Merged into the latest customer, not the one this call was made with:
      // right after a sign-up that is still undefined here, and the response
      // can leave out fields the session has (e.g. stripeCustomerId).
      setCustomer((current) => ({ ...current, ...response.data } as Customer));

      // The merge happens on React's schedule; callers only need success.
      return response.data;
    } catch (err) {
      logger.error({ err }, 'Error updating business name');
    } finally {
      setLoading(false);
    }

    return undefined;
  };

  const updateEmailSubscription = async (
    req: UpdateEmailSubscriptionRequest,
  ): Promise<void> => {
    try {
      const channel = CustomerSubscriptionChannel.EMAIL;

      await axios.post('/api/customer/subscribe', {
        ...req,
        channel,
      });
    } catch (err) {
      logger.error({ err, req }, 'Error updating email subscription');
    }
  };

  const updateSmsSubscription = async (
    req: UpdateSmsSubscriptionRequest,
  ): Promise<void> => {
    try {
      const channel = CustomerSubscriptionChannel.SMS;

      await axios.post('/api/customer/subscribe', {
        ...req,
        channel,
      });
    } catch (err) {
      logger.error({ err, req }, 'Error updating sms subscription');
    }
  };

  const fetchCustomerData = async (): Promise<Customer | undefined> => {
    try {
      if (customer) {
        return undefined;
      }

      setLoading(true);

      const response = await axios.get<Customer>('/api/customer/me');

      if (!response.data) {
        throw new Error('User not found');
      }

      return response.data;
    } catch (err) {
      logger.error({ err }, 'Error fetching customer data');
      clearCustomerData();
    } finally {
      setLoading(false);
    }

    return undefined;
  };

  const verifyCognitoCookies = async (idToken?: string): Promise<void> => {
    logger.debug(
      { initialIdToken, initialCustomer },
      'verifying cognito cookies',
    );
    const _idToken = idToken || initialIdToken;

    if (_idToken && !initialCustomer) {
      const customerData = await fetchCustomerData();

      if (!customerData) {
        return;
      }

      setCustomerData(customerData);
    }
  };

  const isSubscribed = async (channel: 'email' | 'sms', email: string) => {
    try {
      const _isSubscribed = await axios
        .get(`/api/customer/subscribed/${email}/${channel}`)
        .then((res) => res.data);

      return _isSubscribed;
    } catch (err) {
      logger.error(
        { err, email, channel },
        'Error checking customer subscription',
      );

      return false;
    }
  };

  const createAddress = async (address: CustomerAddress) => {
    try {
      setLoading(true);
      setError(null);
      const addresses = await axios
        .post(`/api/customer/addresses`, address)
        .then((res) => res.data);

      return addresses;
    } catch (err: any) {
      logger.error({ err }, 'Error creating address');
      const errorMessage = axios.isAxiosError(err)
        ? (err.response?.data as { message: string })?.message
        : err.message;

      setError(errorMessage || 'Error creating address');
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const softDeleteAddress = async (addressId: string) => {
    try {
      if (!addressId) {
        throw new Error('Address Id is required');
      }
      setLoading(true);
      setError(null);
      const addresses = await axios
        .delete(`/api/customer/addresses?addressId=${addressId}`)
        .then((res) => res.data);

      return addresses;
    } catch (err: any) {
      logger.error({ err, addressId }, 'Error deleting address');

      const errorMessage = axios.isAxiosError(err)
        ? (err.response?.data as { message: string })?.message
        : err.message;

      setError(errorMessage || 'Error deleting address');
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateCustomerAddress = async (
    address: CustomerAddress,
    addressId: string | undefined,
  ) => {
    try {
      if (!addressId) {
        throw new Error('Address Id is required');
      }
      setError(null);
      setLoading(true);
      const addresses = await axios
        .put(`/api/customer/addresses?addressId=${addressId}`, address)
        .then((res) => res.data);

      return addresses;
    } catch (err: any) {
      logger.error({ err, addressId }, 'Error updating address');

      const errorMessage = axios.isAxiosError(err)
        ? (err.response?.data as { message: string })?.message
        : err.message;

      setError(errorMessage || 'Error updating address');
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyCognitoCookies();
  }, []);

  // Cognito Login
  const cognitoLogin = async (
    email: string,
    password?: string,
    authChallenge?: SignInAuthChallenge,
  ): Promise<CognitoCustomerResponse> => {
    try {
      setError(null);
      setLoading(true);

      const response = await axios.post<CognitoCustomerResponse>(
        '/api/customer/auth/cognito/login',
        { email, password, authChallenge },
      );

      if (!response.data || !response.data.message) {
        throw new Error('Authentication failed: No response data');
      }

      if (response.data.idToken) {
        setCognitoIdToken(response.data.idToken);
      }

      if (response.data.refreshToken) {
        setCognitoRefreshToken(response.data.refreshToken);
      }

      if (response.data.customer) {
        setCustomerData(response.data.customer);
      } else if (password && !response.data.authState) {
        // A password attempt that returns an authState (e.g. NO_PASSWORD)
        // is a valid routing signal, not a failure.
        throw new Error('Customer not found');
      }

      return response.data;
    } catch (err: any) {
      const errorMessage = 'Failed to Sign in Customer';

      logger.error({ err }, errorMessage);

      if (err?.response?.data) {
        return err.response.data;
      }

      return { message: errorMessage, error: 'Error' };
    } finally {
      setLoading(false);
    }
  };

  // Cognito Set Password (no-password users; OTP verified)
  const cognitoSetPassword = async (
    email: string,
    otp: string,
    newPassword: string,
  ): Promise<CognitoCustomerResponse> => {
    try {
      setError(null);
      setLoading(true);

      const response = await axios.post<CognitoCustomerResponse>(
        '/api/customer/auth/cognito/set-password',
        { email, otp, newPassword },
      );

      if (!response.data || !response.data.message) {
        throw new Error('Failed to set password: No response data');
      }

      if (response.data.idToken) {
        setCognitoIdToken(response.data.idToken);
      }

      if (response.data.refreshToken) {
        setCognitoRefreshToken(response.data.refreshToken);
      }

      if (response.data.customer) {
        setCustomerData(response.data.customer);
      }

      return response.data;
    } catch (err: any) {
      const errorMessage = 'Failed to set password';

      logger.error({ err }, errorMessage);

      if (err?.response?.data) {
        return err.response.data;
      }

      return { message: errorMessage, error: 'Error' };
    } finally {
      setLoading(false);
    }
  };

  // Cognito Signup
  const cognitoSignup = async (
    customerData: CreateCognitoCustomer,
  ): Promise<CognitoCustomerResponse> => {
    try {
      setError(null);
      setLoading(true);

      const response = await axios.post<CognitoCustomerResponse>(
        '/api/customer/auth/cognito/signup',
        customerData,
      );

      if (!response.data || !response.data.message) {
        throw new Error('Failed to create customer profile: No response data');
      }

      return response.data;
    } catch (err: any) {
      const errorMessage =
        'There was a problem signing up. Please try again later.';

      logger.error({ err }, errorMessage);

      if (err?.response?.data) {
        return err.response.data;
      }

      return { message: errorMessage, error: 'Error' };
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password
  const cognitoForgotPassword = async (
    email: string,
  ): Promise<CognitoCustomerResponse> => {
    try {
      setLoading(true);
      const res = await axios.post<CognitoCustomerResponse>(
        '/api/customer/auth/cognito/forgot',
        { email },
      );

      if (!res.data || !res.data.message) {
        throw new Error('Failed to send forgot password email');
      }

      return res.data;
    } catch (err: any) {
      const errorMessage = 'Error sending forgot password email';

      logger.error({ err }, errorMessage);

      if (err?.response?.data) {
        return err.response.data;
      }

      return { message: errorMessage, error: 'Error' };
    } finally {
      setLoading(false);
    }
  };

  // Reset Password
  const cognitoResetPassword = async (
    email: string,
    password: string,
    otp: string,
  ): Promise<CognitoCustomerResponse> => {
    try {
      setLoading(true);

      const res = await axios.post<CognitoCustomerResponse>(
        '/api/customer/auth/cognito/reset',
        { email, password, otp },
      );

      if (!res.data || !res.data.message) {
        throw new Error('Failed to reset password');
      }

      return res.data;
    } catch (err: any) {
      const errorMessage = 'Error resetting password';

      logger.error({ err }, errorMessage);

      if (err?.response?.data) {
        return err.response.data;
      }

      return { message: errorMessage, error: 'Error' };
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const cognitoVerifyOtp = async (
    email: string,
    otp: string,
  ): Promise<CognitoCustomerResponse> => {
    try {
      setLoading(true);

      const res = await axios.post<CognitoCustomerResponse>(
        '/api/customer/auth/cognito/otp/verify',
        { email, otp },
      );

      if (!res.data || !res.data.message) {
        throw new Error('Failed to verify OTP');
      }

      if (!res.data.idToken || !res.data.refreshToken) {
        throw new Error('Failed to verify OTP: No tokens returned');
      }

      setCognitoIdToken(res.data.idToken);
      setCognitoRefreshToken(res.data.refreshToken);

      if (res.data.customer) {
        setCustomerData(res.data.customer);
      } else {
        throw new Error('Customer not found');
      }

      return res.data;
    } catch (err: any) {
      const errorMessage = 'Error verifying OTP';

      logger.error({ err }, errorMessage);

      if (err?.response?.data) {
        return err.response.data;
      }

      return { message: errorMessage, error: 'Error' };
    } finally {
      setLoading(false);
    }
  };

  const cognitoResendOtp = async (
    email: string,
  ): Promise<CognitoCustomerResponse> => {
    try {
      setLoading(true);

      const res = await axios.post<CognitoCustomerResponse>(
        '/api/customer/auth/cognito/otp/resend',
        { email },
      );

      if (!res.data || !res.data.message) {
        throw new Error('Failed to resend OTP');
      }

      return res.data;
    } catch (err: any) {
      const errorMessage = 'Error resending OTP';

      logger.error({ err }, errorMessage);

      if (err?.response?.data) {
        return err.response.data;
      }

      return { message: errorMessage, error: 'Error' };
    } finally {
      setLoading(false);
    }
  };

  const cognitoRefresh = async (): Promise<
    CognitoRefreshTokenResponse | { message: string; error: string }
  > => {
    const refreshToken = cookies[COOKIE_COGNITO_REFRESH_TOKEN];

    if (!refreshToken) {
      return { message: 'No refresh token found', error: 'Error' };
    }

    try {
      setLoading(true);
      const res = await axios.post<CognitoRefreshTokenResponse>(
        '/api/customer/auth/cognito/refresh',
        { refreshToken },
      );

      if (!res.data || !res.data.id_token) {
        throw new Error('Failed to refresh AWS Cognito tokens');
      }

      setCognitoIdToken(res.data.id_token);

      return res.data;
    } catch (err: any) {
      logger.error({ err }, 'Error refreshing AWS Cognito tokens');

      return { message: 'Error refreshing AWS Cognito tokens', error: 'Error' };
    } finally {
      setLoading(false);
    }
  };

  // Complete Google Auth
  const cognitoGoogleAuthComplete = async (
    code: string,
  ): Promise<AuthTokens | null> => {
    try {
      setLoading(true);

      const response = await fetch(
        '/api/customer/auth/cognito/google/code-for-token',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to exchange code for token.`);
      }

      const data: AuthTokens = await response.json();

      if (data.id_token && data.refresh_token) {
        // Complete Google Auth
        const authResponse = await fetch('/api/customer/auth/cognito/google', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken: data.id_token }),
        });

        if (!authResponse.ok) {
          throw new Error('Auth completion failed.');
        }

        setCognitoIdToken(data.id_token);
        setCognitoRefreshToken(data.refresh_token);
        verifyCognitoCookies(data.id_token);
      }

      return data;
    } catch (err) {
      logger.error({ err }, 'Unable to Complete Google Auth');

      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    customer,
    anonymousId,
    loading,
    error,

    // Api
    cognitoLogin,
    cognitoSetPassword,
    cognitoSignup,
    cognitoForgotPassword,
    cognitoResetPassword,
    cognitoVerifyOtp,
    cognitoResendOtp,
    cognitoRefresh,
    cognitoGoogleAuthComplete,
    logout,
    updateAddress,
    updateBusinessName,
    fetchCustomerData,
    updateEmailSubscription,
    updateSmsSubscription,
    isSubscribed,
    createAddress,
    softDeleteAddress,
    updateCustomerAddress,
  };
};

export default useCustomer;
